import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { apiList, baseUrl } from "../../helpers/apiList";
import { axiosGetApi, axiosPostApi } from "../../helpers/interceptorServices";
import CustomAlert from "../../helpers/CustomAlert";
import PanInput from "../common/PanInput";
import AadhaarInput from "../common/AadhaarInput";
import {
  GSTIN_FORMAT,
  MAX_DOC_KB,
  grid,
  inputClass,
  addressError,
  celebrate,
  fileError,
  Field,
  Choice,
  DocumentTile,
  ReviewSections,
  WizardShell,
} from "../common/OnboardingWizard";

// Step-by-step LMBP onboarding (T10). Replaces the single dense form for
// "Add LMBP" only; edit / view / authorise keep the existing form. It sends the
// same request as before: the "request" JSON built from the same keys, plus the
// optional document parts, to /master/customer/create.

const STEPS = [
  { key: "business", title: "Business", hint: "Start with the GST number" },
  { key: "contact", title: "Contact & address", hint: "Who we talk to, and where" },
  { key: "network", title: "Network & pricing", hint: "Hub or master LMBP, rate card" },
  { key: "kyc", title: "KYC & documents", hint: "ID numbers and uploads" },
  { key: "review", title: "Review", hint: "Check and submit" },
];

const COMPANY_TYPES = ["Proprietor", "Private Limited", "Individual", "LLP", "Public Limited"];
const BUSINESS_TYPES = [
  { value: "OUTLET", label: "Outlet" },
  { value: "SELLER", label: "Seller" },
];

const DOCUMENTS = [
  { key: "gstCertificate", label: "GST Certificate" },
  { key: "aadharFront", label: "Aadhaar front" },
  { key: "aadharBack", label: "Aadhaar back" },
  { key: "panTan", label: "LMBP PAN card" },
];
const emptyPayload = (username) => ({
  username,
  name: "",
  emailId: "",
  address1: "",
  address2: "",
  address3: "",
  city: "",
  state: "",
  pinCode: "",
  firstContactPersonName: "",
  firstContactPersonMobileNo: "",
  bankName: "",
  accountNo: "",
  ifscCode: "",
  gstNumber: "",
  aadharNumber: "",
  panNumber: "",
  companyPanNumber: "",
  companyType: "",
  hubCode: "",
  hubName: "",
  lmbpBusinessType: "",
  isEcommerce: "",
  masterLmbpCode: "",
  hierarchyType: "",
  parentType: "",
  margin: "",
  marginCompanyShare: 50,
  marginPayout: "BOOKING",
});

// ---------------- field rules (same rules and messages as the old form) ----------------

const RULES = {
  gstNumber: (p) =>
    p.gstNumber && !GSTIN_FORMAT.test(p.gstNumber) ? "Invalid GST Number format" : null,
  // GST and the LMBP PAN are optional (the PAN is filled from the GST number and can be changed)
  companyPanNumber: (p) => {
    const v = (p.companyPanNumber || "").trim();
    if (!v) return p.gstNumber && p.gstNumber.length !== 15 ? "Enter the full GST number, or clear it and enter the PAN" : null;
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v)) return "Invalid PAN format (e.g. ABCDE1234F)";
    return null;
  },
  name: (p) => {
    const v = (p.name || "").trim();
    if (!v) return "LMBP Name is required";
    if (!/^[A-Za-z0-9@&.,'\- ]+$/.test(v) || v.length < 2 || v.length > 50)
      return "LMBP name must be valid and between 2 and 50 characters";
    if (/^(.)\1{2,}$/.test(v)) return "LMBP name cannot be made of a single repeated character";
    return null;
  },
  emailId: (p) => {
    const v = p.emailId || "";
    if (!v) return "Email is required";
    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(v) ||
      /\.com\./.test(v) ||
      /\.co\.[a-zA-Z]{2}\./.test(v)
    )
      return "Enter valid email ending with .com .co.xx or .in";
    return null;
  },
  companyType: (p) => (p.companyType ? null : "LMBP Type is required"),
  lmbpBusinessType: (p) => (p.lmbpBusinessType ? null : "LMBP Business Type is required"),
  firstContactPersonName: (p) => {
    const v = (p.firstContactPersonName || "").trim();
    if (!v) return "Contact person name is required";
    if (!/^[A-Za-z@&.,'\- ]+$/.test(v) || v.length < 2 || v.length > 50)
      return "Contact person name must be between 2 and 50 characters and contain only letters";
    if (!/^[A-Za-z][A-Za-z .-]*[A-Za-z]$/.test(v))
      return "Contact person name must contain only letters, spaces, dots, or hyphens and cannot start or end with spaces or symbols";
    if (/^([A-Za-z])\1+$/.test(v.replace(/[ .-]/g, "")))
      return "Contact person name cannot consist of the same letter repeated";
    return null;
  },
  firstContactPersonMobileNo: (p) => {
    const v = p.firstContactPersonMobileNo || "";
    if (!v) return "Contact person mobile number is required";
    if (!/^[6789]\d{9}$/.test(v)) return "Mobile number must be 10 digits and start with 6, 7, 8, or 9";
    if (/^(\d)\1{9}$/.test(v)) return "Mobile number cannot have all repeated digits";
    if (v === "1234567890" || v === "9876543210") return "Mobile number cannot be a simple sequence";
    return null;
  },
  pinCode: (p) => {
    if (!p.pinCode) return "Pincode is required";
    if (!/^[1-9]\d{5}$/.test(p.pinCode)) return "Pincode must be 6 digits and must not start with 0";
    return null;
  },
  address1: (p) => addressError(p.address1, 1, true),
  address2: (p) => addressError(p.address2, 2, true),
  address3: (p) => addressError(p.address3, 3, false),
  hierarchyType: (p) => (p.hierarchyType ? null : "Hierarchy Type is required"),
  parentType: (p) => (p.parentType ? null : "Parent Type is required"),
  hubCode: (p) =>
    p.hierarchyType === "MASTER" || (p.hierarchyType === "SUB" && p.parentType === "HUB")
      ? p.hierarchyType === "MASTER" && !p.hubCode
        ? "HUB Type is required"
        : null
      : null,
  masterLmbpCode: (p) =>
    p.hierarchyType === "SUB" && p.parentType === "LMBP" && !p.masterLmbpCode
      ? "Master LMBP Name is required"
      : null,
  rateCardId: (p) => (p.rateCardId ? null : "Rate Card Type is required"),
  isEcommerce: (p) =>
    p.isEcommerce === "" || p.isEcommerce === undefined || p.isEcommerce === null
      ? "Ecommerce selection is required"
      : null,
  // Margin (%) on freight: required for a sub-LMBP (default 10), optional for a master (default 0)
  margin: (p) => {
    const empty = p.margin === "" || p.margin === null || p.margin === undefined;
    if (empty) return p.hierarchyType === "SUB" ? "Margin is required" : null;
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(String(p.margin))) return "Margin can have up to 2 decimal places";
    const n = Number(p.margin);
    if (n < 0 || n > 30) return "Margin must be between 0 and 30%";
    return null;
  },
  marginCompanyShare: (p) => {
    if (p.hierarchyType !== "SUB") return null;
    const v = p.marginCompanyShare;
    if (v === "" || v === null || v === undefined) return "Company share is required";
    const n = Number(v);
    if (Number.isNaN(n) || n < 0 || n > 100) return "Company share must be between 0 and 100%";
    return null;
  },
  panNumber: (p) => {
    const raw = (p.panNumberRaw || "").trim();
    if (!raw) return "PAN Number is required";
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(raw)) return "Invalid PAN Number format";
    if (p.companyPanNumber && raw === p.companyPanNumber) return "LMBP PAN and Authorised PAN must be different";
    return null;
  },
  aadharNumber: (p) => {
    const raw = (p.aadharNumberRaw || "").trim();
    if (!raw) return "Aadhar Number is required";
    if (!/^[2-9]\d{11}$/.test(raw)) return "Aadhar Number must be exactly 12 digits and start with 2-9";
    if (/^(\d)\1{11}$/.test(raw)) return "Aadhar Number cannot have all repeated digits";
    return null;
  },
};

const STEP_FIELDS = {
  business: ["gstNumber", "companyPanNumber", "name", "emailId", "companyType", "lmbpBusinessType"],
  contact: ["firstContactPersonName", "firstContactPersonMobileNo", "pinCode", "address1", "address2", "address3"],
  network: ["hierarchyType", "parentType", "hubCode", "masterLmbpCode", "rateCardId", "isEcommerce", "margin", "marginCompanyShare"],
  kyc: ["panNumber", "aadharNumber"],
  review: [],
};

// Which step a backend error belongs to, so the user lands where they can fix it
const stepForServerMessage = (msg = "") => {
  const m = msg.toLowerCase();
  if (m.includes("mobile")) return 1;
  if (m.includes("aadhaar") || m.includes("aadhar")) return 3;
  if (m.includes("email") || m.includes("pan") || m.includes("gst")) return 0;
  if (m.includes("hub") || m.includes("master lmbp") || m.includes("rate card")) return 2;
  return null;
};

// ---------------- wizard ----------------

const LmbpOnboardingWizard = ({ onClose, onCreated }) => {
  const { user } = useSelector((state) => state.auth);
  const username = user?.data?.username;

  const [step, setStep] = useState(0);
  const [p, setP] = useState(() => emptyPayload(username));
  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [errors, setErrors] = useState({});
  const [gst, setGst] = useState(null); // lookup result for the current GSTIN
  const [gstChecking, setGstChecking] = useState(false);
  const [pinState, setPinState] = useState(""); // "", "loading", "ok", "error"
  const [hubs, setHubs] = useState([]);
  const [masters, setMasters] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // ---- reference data ----
  useEffect(() => {
    axiosGetApi(apiList.ALL_ACTIVE_HUB_DATA, { username })
      .then((r) => r?.data?.respCode === "00" && setHubs(r.data.data || []))
      .catch(() => {});
    axiosGetApi(`${apiList.FETCH_ALL_MASTER_LMBP_LIST}?username=${username}`)
      .then((r) => r?.status === 200 && setMasters(r.data.data || []))
      .catch(() => {});
    axiosGetApi(`${apiList.ALL_RATE_CARD_DATA}?username=${username}`)
      .then((r) => r?.data?.respCode === "00" && setRateCards(r.data.data || []))
      .catch(() => {});
  }, [username]);

  const set = (changes) => setP((prev) => ({ ...prev, ...changes }));
  const clearError = (...keys) =>
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  const checkField = (key) => {
    const msg = RULES[key]?.(p);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
    return msg;
  };

  // ---- GST: derive PAN and state, check duplicates as soon as 15 chars are in ----
  const onGstChange = (raw) => {
    const value = raw.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);
    setP((prev) => ({
      ...prev,
      gstNumber: value,
      // a PAN taken from the GST goes when the GST changes; one typed by hand (no GST) stays
      companyPanNumber:
        value.length === 15 ? value.slice(2, 12) : prev.gstNumber.length === 15 ? "" : prev.companyPanNumber,
    }));
    clearError("gstNumber", "companyPanNumber");
    setGst(null);
    if (value.length === 15) lookupGst(value);
  };

  // PAN (typed or filled from the GST): format first, then whether another LMBP already has it
  const [panChecking, setPanChecking] = useState(false);
  const checkPan = async () => {
    if (checkField("companyPanNumber") || !p.companyPanNumber) return;
    setPanChecking(true);
    try {
      const r = await axiosGetApi(`${apiList.GET_COMPANY_PAN_STATUS}/${p.companyPanNumber}`);
      if (r?.data?.respCode === "00") {
        setErrors((e) => ({ ...e, companyPanNumber: "This PAN is already registered to another LMBP" }));
      }
    } catch {
      // the server checks again on submit
    } finally {
      setPanChecking(false);
    }
  };

  const lookupGst = async (value) => {
    if (!GSTIN_FORMAT.test(value)) {
      setErrors((e) => ({ ...e, gstNumber: "Invalid GST Number format" }));
      return;
    }
    setGstChecking(true);
    try {
      const r = await axiosGetApi(`${apiList.GST_LOOKUP}/${value}`);
      const d = r?.data;
      if (d?.respCode === "00") {
        setGst(d.data);
        if (d.data.gstRegistered || d.data.panRegistered) {
          setErrors((e) => ({
            ...e,
            gstNumber: d.data.gstRegistered
              ? "This GST number is already registered with another LMBP."
              : "An LMBP with this PAN is already registered.",
          }));
        }
      } else {
        setErrors((e) => ({ ...e, gstNumber: d?.message || "Invalid GST Number" }));
      }
    } catch (e) {
      // Lookup is a convenience; the format check above still applies
    } finally {
      setGstChecking(false);
    }
  };

  // ---- pincode → city / state (same lookup as before) ----
  const lookupPincode = (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    setPinState("loading");
    axios
      .get(`${baseUrl}/sms/pincode/${pin}`)
      .then((r) => {
        const { status, message, data } = r.data || {};
        if (!status || !data || data.length === 0) {
          set({ city: "", state: "" });
          setPinState("error");
          setErrors((e) => ({ ...e, pinCode: message || "Enter a valid Pincode" }));
        } else {
          set({ city: data[0].cityName, state: data[0].state });
          setPinState("ok");
          clearError("pinCode");
        }
      })
      .catch(() => {
        setPinState("error");
        setErrors((e) => ({ ...e, pinCode: "Error fetching pincode data" }));
      });
  };

  // ---- network choices (same side effects as before; a hub is cleared when a master LMBP is the parent) ----
  const chooseHierarchy = (type) => {
    clearError("hierarchyType", "parentType", "hubCode", "masterLmbpCode", "margin");
    if (type === "MASTER") {
      set({ hierarchyType: "MASTER", parentType: "HUB", margin: 0, masterLmbpCode: "" });
    } else {
      setP((prev) => ({ ...prev, hierarchyType: "SUB", parentType: "", margin: Number(prev.margin) > 0 ? prev.margin : 10 }));
    }
  };
  const chooseParent = (type) => {
    clearError("parentType", "hubCode", "masterLmbpCode");
    if (type === "LMBP") set({ parentType: "LMBP", hubCode: "", hubName: "" });
    else set({ parentType: "HUB", masterLmbpCode: "" });
  };
  const inheritsFromMaster = p.hierarchyType === "SUB" && p.parentType === "LMBP";

  // ---- documents ----
  const onFile = (key, file) => {
    if (!file) return;
    const err = fileError(file);
    setFileErrors((e) => ({ ...e, [key]: err }));
    if (!err) setFiles((f) => ({ ...f, [key]: file }));
  };
  const removeFile = (key) =>
    setFiles((f) => {
      const next = { ...f };
      delete next[key];
      return next;
    });

  // ---- navigation ----
  const validateStep = (index) => {
    const found = {};
    STEP_FIELDS[STEPS[index].key].forEach((k) => {
      const msg = RULES[k]?.(p);
      if (msg) found[k] = msg;
    });
    // A registered GSTIN / PAN found by the lookup blocks the step too
    if (index === 0 && gst && (gst.gstRegistered || gst.panRegistered) && errors.gstNumber)
      found.gstNumber = errors.gstNumber;
    if (index === 0 && p.gstNumber && p.gstNumber.length !== 15)
      found.gstNumber = "GST number must be 15 characters (or leave it empty).";
    return found;
  };

  const next = () => {
    const found = validateStep(step);
    setErrors((prev) => ({ ...prev, ...found }));
    if (Object.keys(found).length) {
      setTimeout(() => document.querySelector(".lmbp-wizard .error")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setStep((s) => s + 1);
  };

  const goTo = (index) => {
    // Free movement backwards; forwards only through valid steps
    if (index <= step) return setStep(index);
    for (let i = step; i < index; i++) {
      const found = validateStep(i);
      if (Object.keys(found).length) {
        setErrors((prev) => ({ ...prev, ...found }));
        setStep(i);
        return;
      }
    }
    setStep(index);
  };

  const submit = async () => {
    for (let i = 0; i < STEPS.length - 1; i++) {
      const found = validateStep(i);
      if (Object.keys(found).length) {
        setErrors((prev) => ({ ...prev, ...found }));
        setStep(i);
        return;
      }
    }
    // Same body as the previous form: payload with trimmed text fields
    const request = {
      ...p,
      name: p.name.trim(),
      address1: p.address1.trim(),
      address2: p.address2.trim(),
      address3: p.address3.trim(),
      firstContactPersonName: p.firstContactPersonName.trim(),
    };
    const formData = new FormData();
    formData.append("request", JSON.stringify(request));
    DOCUMENTS.forEach((d) => files[d.key] && formData.append(d.key, files[d.key]));

    setSubmitting(true);
    try {
      const r = await axiosPostApi(apiList.CREATE_CUSTOMER_DATA, formData);
      if (r?.status === 200 && r?.data?.status === true) {
        celebrate();
        await CustomAlert({
          title: "Success",
          text: r.data.message || "LMBP added successfully",
          icon: "success",
          confirmButtonText: "Ok",
        });
        onCreated && onCreated();
        onClose();
      } else {
        const msg = r?.data?.message || "There was an issue processing the request.";
        const back = stepForServerMessage(msg);
        CustomAlert({ title: "Could not create LMBP", text: msg, icon: "error", confirmButtonText: "OK" });
        if (back !== null) setStep(back);
      }
    } catch (e) {
      CustomAlert({ title: "Error", text: "An error occurred while processing.", icon: "error", confirmButtonText: "Retry" });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRateCard = rateCards.find((r) => String(r.rateCardId) === String(p.rateCardId));
  const gstStateMismatch =
    gst?.state && p.state && pinState === "ok" && gst.state.toUpperCase() !== String(p.state).toUpperCase();

  // ---------------- steps ----------------

  const renderBusiness = () => (
    <div style={grid}>
      <Field
        label="GST number"
        error={errors.gstNumber}
        hint={
          gstChecking
            ? "Checking…"
            : gst
              ? `Valid GSTIN · ${gst.state || "state " + gst.stateCode}`
              : "Optional. The PAN is taken from it; nothing is fetched from outside."
        }
        full
      >
        <input
          className={inputClass(errors.gstNumber)}
          value={p.gstNumber}
          placeholder="e.g. 27ABCDE1234F1Z5"
          maxLength={15}
          autoFocus
          onChange={(e) => onGstChange(e.target.value)}
          onBlur={() => p.gstNumber && p.gstNumber.length !== 15 && setErrors((x) => ({ ...x, gstNumber: "GST number must be 15 characters (or leave it empty)." }))}
          style={{ fontFamily: "monospace", letterSpacing: "1px", maxWidth: "360px" }}
        />
      </Field>
      <Field
        label="LMBP PAN"
        error={errors.companyPanNumber}
        hint={
          panChecking
            ? "Checking…"
            : p.gstNumber
              ? null
              : "Optional when there is no GST number"
        }
      >
        <input
          className={inputClass(errors.companyPanNumber)}
          value={p.companyPanNumber}
          placeholder={p.gstNumber ? "Filled from the GST number" : "e.g. ABCDE1234F"}
          maxLength={10}
          onChange={(e) => {
            set({ companyPanNumber: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 10) });
            clearError("companyPanNumber");
          }}
          onBlur={() => checkPan()}
          style={{ fontFamily: "monospace", letterSpacing: "1px" }}
        />
      </Field>
      <Field label="LMBP name" required error={errors.name}>
        <input
          className={inputClass(errors.name)}
          value={p.name}
          placeholder="Business name"
          onChange={(e) => {
            set({ name: e.target.value.toUpperCase() });
            clearError("name");
          }}
          onBlur={() => checkField("name")}
        />
      </Field>
      <Field label="Email" required error={errors.emailId}>
        <input
          type="email"
          className={inputClass(errors.emailId)}
          value={p.emailId}
          placeholder="name@business.com"
          onChange={(e) => {
            set({ emailId: e.target.value.toLowerCase().trim() });
            clearError("emailId");
          }}
          onBlur={() => checkField("emailId")}
        />
      </Field>
      <Field label="LMBP type" required error={errors.companyType}>
        <select
          className={inputClass(errors.companyType)}
          value={p.companyType}
          onChange={(e) => {
            set({ companyType: e.target.value });
            clearError("companyType");
          }}
        >
          <option value="">Select type</option>
          {COMPANY_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Business type" required error={errors.lmbpBusinessType}>
        <div className="d-flex" style={{ gap: "10px", flexWrap: "wrap" }}>
          {BUSINESS_TYPES.map((t) => (
            <Choice
              key={t.value}
              selected={p.lmbpBusinessType === t.value}
              title={t.label}
              text={t.value === "OUTLET" ? "Walk-in booking point" : "Books its own shipments"}
              onClick={() => {
                set({ lmbpBusinessType: t.value });
                clearError("lmbpBusinessType");
              }}
            />
          ))}
        </div>
      </Field>
    </div>
  );

  const renderContact = () => (
    <div style={grid}>
      <Field label="Contact person" required error={errors.firstContactPersonName}>
        <input
          className={inputClass(errors.firstContactPersonName)}
          value={p.firstContactPersonName}
          maxLength={50}
          placeholder="Full name"
          onChange={(e) => {
            if (/^[A-Za-z .-]*$/.test(e.target.value)) {
              set({ firstContactPersonName: e.target.value });
              clearError("firstContactPersonName");
            }
          }}
          onBlur={() => checkField("firstContactPersonName")}
        />
      </Field>
      <Field label="Mobile number" required error={errors.firstContactPersonMobileNo}>
        <input
          className={inputClass(errors.firstContactPersonMobileNo)}
          value={p.firstContactPersonMobileNo}
          inputMode="numeric"
          maxLength={10}
          placeholder="10-digit mobile"
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 10);
            set({ firstContactPersonMobileNo: v });
            clearError("firstContactPersonMobileNo");
          }}
          onBlur={() => checkField("firstContactPersonMobileNo")}
        />
      </Field>
      <Field
        label="Pincode"
        required
        error={errors.pinCode}
        hint={
          pinState === "loading"
            ? "Looking up…"
            : pinState === "ok"
              ? `${p.city}, ${p.state}`
              : "City and state are filled from it"
        }
      >
        <input
          className={inputClass(errors.pinCode)}
          value={p.pinCode}
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit pincode"
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 6);
            set({ pinCode: v });
            clearError("pinCode");
            if (v.length === 6) lookupPincode(v);
            else setPinState("");
          }}
          onBlur={() => checkField("pinCode")}
        />
      </Field>
      <Field label="City / State">
        <input className={inputClass(null, true)} readOnly value={p.city ? `${p.city}, ${p.state}` : ""} placeholder="—" />
        {gstStateMismatch && (
          <div style={{ color: "#b45309", fontSize: "12px", marginTop: "4px" }}>
            The GST number is registered in {gst.state}; this pincode is in {p.state}.
          </div>
        )}
      </Field>
      {[1, 2, 3].map((n) => (
        <Field key={n} label={`Address line ${n}`} required={n < 3} error={errors[`address${n}`]} full={n === 1}>
          <input
            className={inputClass(errors[`address${n}`])}
            value={p[`address${n}`]}
            maxLength={150}
            placeholder={n === 1 ? "Shop / building, street" : n === 2 ? "Area, landmark" : "Optional"}
            onChange={(e) => {
              set({ [`address${n}`]: e.target.value });
              clearError(`address${n}`);
            }}
            onBlur={() => checkField(`address${n}`)}
          />
        </Field>
      ))}
    </div>
  );

  const renderNetwork = () => (
    <div style={grid}>
      <Field label="This LMBP is a" required error={errors.hierarchyType} full>
        <div className="d-flex" style={{ gap: "10px", flexWrap: "wrap" }}>
          <Choice selected={p.hierarchyType === "MASTER"} title="Master LMBP" text="Reports to a hub" onClick={() => chooseHierarchy("MASTER")} />
          <Choice selected={p.hierarchyType === "SUB"} title="Sub-LMBP" text="Works under a hub or a master LMBP, with a margin" onClick={() => chooseHierarchy("SUB")} />
        </div>
      </Field>

      {p.hierarchyType === "SUB" && (
        <Field label="Parent" required error={errors.parentType} full>
          <div className="d-flex" style={{ gap: "10px", flexWrap: "wrap" }}>
            <Choice selected={p.parentType === "HUB"} title="A hub" text="Sits directly under a courier hub" onClick={() => chooseParent("HUB")} />
            <Choice selected={p.parentType === "LMBP"} title="A master LMBP" text="Inherits its rate card and e-commerce setting" onClick={() => chooseParent("LMBP")} />
          </div>
        </Field>
      )}

      {(p.hierarchyType === "MASTER" || (p.hierarchyType === "SUB" && p.parentType === "HUB")) && (
        <Field label="Hub" required={p.hierarchyType === "MASTER"} error={errors.hubCode}>
          <select
            className={inputClass(errors.hubCode)}
            value={p.hubCode}
            onChange={(e) => {
              const h = hubs.find((x) => x.hubCode === e.target.value);
              set({ hubCode: h?.hubCode || "", hubName: h?.hubName || "" });
              clearError("hubCode");
            }}
          >
            <option value="">Select hub</option>
            {hubs.map((h) => (
              <option key={h.hubCode} value={h.hubCode}>
                {h.hubName} ({h.hubCode})
              </option>
            ))}
          </select>
        </Field>
      )}

      {inheritsFromMaster && (
        <Field label="Master LMBP" required error={errors.masterLmbpCode}>
          <select
            className={inputClass(errors.masterLmbpCode)}
            value={p.masterLmbpCode}
            onChange={(e) => {
              const m = masters.find((x) => x.customerCode === e.target.value);
              set({
                masterLmbpCode: m?.customerCode || "",
                rateCardId: m?.rateCardId || "",
                rateCardName: m?.rateCardName || "",
                isEcommerce: m ? m.isEcommerce : "",
              });
              clearError("masterLmbpCode", "rateCardId", "isEcommerce");
            }}
          >
            <option value="">Select master LMBP</option>
            {masters.map((m) => (
              <option key={m.customerCode} value={m.customerCode}>
                {m.name} ({m.customerCode})
              </option>
            ))}
          </select>
        </Field>
      )}

      {p.hierarchyType && (
        <>
          <Field
            label="Rate card"
            required
            error={errors.rateCardId}
            hint={inheritsFromMaster ? "From the master LMBP" : undefined}
          >
            {inheritsFromMaster ? (
              <input className={inputClass(null, true)} readOnly value={p.rateCardName || p.rateCardId || ""} placeholder="Pick a master LMBP" />
            ) : (
              <select
                className={inputClass(errors.rateCardId)}
                value={p.rateCardId || ""}
                onChange={(e) => {
                  const rc = rateCards.find((r) => String(r.rateCardId) === e.target.value);
                  set({ rateCardId: rc?.rateCardId || "", rateCardName: rc?.rateCardName || "" });
                  clearError("rateCardId");
                }}
              >
                <option value="">Select rate card</option>
                {rateCards.map((r) => (
                  <option key={r.rateCardId} value={r.rateCardId}>
                    {r.rateCardName}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field
            label="E-commerce"
            required
            error={errors.isEcommerce}
            hint={inheritsFromMaster ? "From the master LMBP" : undefined}
          >
            <div className="d-flex" style={{ gap: "10px" }}>
              {[
                { v: true, t: "Yes" },
                { v: false, t: "No" },
              ].map((o) => (
                <Choice
                  key={o.t}
                  selected={p.isEcommerce === o.v}
                  title={o.t}
                  text={o.v ? "Ships for online sellers" : "Counter bookings only"}
                  disabled={inheritsFromMaster}
                  onClick={() => {
                    set({ isEcommerce: o.v });
                    clearError("isEcommerce");
                  }}
                />
              ))}
            </div>
          </Field>
        </>
      )}

      {p.hierarchyType && (
        <Field
          label="Margin (%)"
          required={p.hierarchyType === "SUB"}
          error={errors.margin}
          hint={
            p.hierarchyType === "SUB"
              ? "On freight, added to the parent's rate (0–30%)"
              : "On freight, all to Appan (0–30%, usually 0)"
          }
        >
          <input
            type="number"
            min={0}
            max={30}
            step={0.01}
            className={inputClass(errors.margin)}
            value={p.margin ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (/^\d{0,2}(\.\d{0,2})?$/.test(v) && (v === "" || Number(v) <= 30)) {
                set({ margin: v });
                clearError("margin");
              }
            }}
            onBlur={() => checkField("margin")}
          />
        </Field>
      )}

      {p.hierarchyType === "SUB" && (
        <>
          <Field
            label="Company share of margin (%)"
            required
            error={errors.marginCompanyShare}
            hint="The parent LMBP gets the rest (default 50/50)"
          >
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              className={inputClass(errors.marginCompanyShare)}
              value={p.marginCompanyShare ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d{0,3}(\.\d{0,2})?$/.test(v) && (v === "" || Number(v) <= 100)) {
                  set({ marginCompanyShare: v });
                  clearError("marginCompanyShare");
                }
              }}
              onBlur={() => checkField("marginCompanyShare")}
            />
          </Field>
          <Field label="Pay the parent LMBP's share" hint="Credited to the parent LMBP's wallet">
            <select
              className={inputClass()}
              value={p.marginPayout || "BOOKING"}
              onChange={(e) => set({ marginPayout: e.target.value })}
            >
              <option value="BOOKING">At booking</option>
              <option value="DELIVERY">After delivery</option>
            </select>
          </Field>
        </>
      )}
    </div>
  );

  const renderKyc = () => (
    <>
      <div style={grid}>
        <Field label="Authorised person's PAN" required error={errors.panNumber} hint="Must differ from the LMBP PAN">
          <PanInput
            value={p.panNumber}
            error={errors.panNumber}
            onChange={(masked, raw) => {
              set({ panNumber: masked, panNumberRaw: raw });
              clearError("panNumber");
            }}
            onBlur={() => checkField("panNumber")}
          />
        </Field>
        <Field label="Aadhaar number" required error={errors.aadharNumber}>
          <AadhaarInput
            value={p.aadharNumber}
            error={errors.aadharNumber}
            onChange={(masked, raw) => {
              set({ aadharNumber: masked, aadharNumberRaw: raw });
              clearError("aadharNumber");
            }}
            onBlur={() => checkField("aadharNumber")}
          />
        </Field>
      </div>

      <div style={{ marginTop: "22px" }}>
        <div className="font-medium" style={{ marginBottom: "4px" }}>
          Documents <span style={{ color: "#6b7280", fontWeight: 400, fontSize: "13px" }}>· optional · PDF or image, up to {MAX_DOC_KB} KB each</span>
        </div>
        <div style={{ ...grid, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          {DOCUMENTS.map((d) => (
            <DocumentTile
              key={d.key}
              label={d.label}
              file={files[d.key]}
              error={fileErrors[d.key]}
              onFile={(f) => onFile(d.key, f)}
              onRemove={() => removeFile(d.key)}
            />
          ))}
        </div>
      </div>
    </>
  );

  const reviewSections = useMemo(
    () => [
      {
        step: 0,
        title: "Business",
        rows: [
          ["GST number", p.gstNumber || "Not given"],
          ["LMBP PAN", p.companyPanNumber || "—"],
          ["Name", p.name],
          ["Email", p.emailId],
          ["LMBP type", p.companyType],
          ["Business type", BUSINESS_TYPES.find((b) => b.value === p.lmbpBusinessType)?.label],
        ],
      },
      {
        step: 1,
        title: "Contact & address",
        rows: [
          ["Contact person", p.firstContactPersonName],
          ["Mobile", p.firstContactPersonMobileNo],
          ["Address", [p.address1, p.address2, p.address3].filter(Boolean).join(", ")],
          ["City / State / PIN", [p.city, p.state, p.pinCode].filter(Boolean).join(", ")],
        ],
      },
      {
        step: 2,
        title: "Network & pricing",
        rows: [
          ["Type", p.hierarchyType === "SUB" ? `Sub-LMBP under a ${p.parentType === "LMBP" ? "master LMBP" : "hub"}` : "Master LMBP"],
          inheritsFromMaster
            ? ["Master LMBP", (masters.find((m) => m.customerCode === p.masterLmbpCode)?.name || "") + ` (${p.masterLmbpCode})`]
            : ["Hub", p.hubName ? `${p.hubName} (${p.hubCode})` : "—"],
          ["Rate card", p.rateCardName || selectedRateCard?.rateCardName || "—"],
          ["E-commerce", p.isEcommerce === true ? "Yes" : p.isEcommerce === false ? "No" : "—"],
          ["Margin", `${p.margin || 0}%`],
          ...(p.hierarchyType === "SUB"
            ? [
                ["Company / parent share", `${p.marginCompanyShare}% / ${100 - Number(p.marginCompanyShare || 0)}%`],
                ["Parent share paid", p.marginPayout === "DELIVERY" ? "After delivery" : "At booking"],
              ]
            : []),
        ],
      },
      {
        step: 3,
        title: "KYC & documents",
        rows: [
          ["Authorised PAN", p.panNumber],
          ["Aadhaar", p.aadharNumber],
          ["Documents", DOCUMENTS.filter((d) => files[d.key]).map((d) => d.label).join(", ") || "None"],
        ],
      },
    ],
    [p, files, masters, inheritsFromMaster, selectedRateCard],
  );

  const renderReview = () => <ReviewSections sections={reviewSections} onEdit={setStep} />;

  const body = [renderBusiness, renderContact, renderNetwork, renderKyc, renderReview][step]();

  return (
    <WizardShell
      className="lmbp-wizard"
      title="Add LMBP"
      steps={STEPS}
      step={step}
      onStepClick={goTo}
      onClose={onClose}
      onBack={() => setStep(step - 1)}
      onNext={next}
      onSubmit={submit}
      nextDisabled={gstChecking}
      submitting={submitting}
      submitLabel="Create LMBP"
      submittingLabel="Creating…"
    >
      {body}
    </WizardShell>
  );
};

export default LmbpOnboardingWizard;
